import type { IntegratedChannel, OpenChannelOrder } from '@wms/domain';
import { useEffect, useState } from 'react';
import { api, errorDetails, errorMessage } from '../../api';
import { fmt } from '../../format';
import { useSession } from '../../session';
import type { CredentialView } from '../../types';
import { Empty, ErrorBanner, Modal, Spinner, useLoad, useToast } from '../../ui';
import { Icon } from '../../ui/icons';
import { ApiKeys } from './ApiKeys';
import { StockSyncDialog } from './StockSync';

interface Field {
  readonly key: string;
  readonly label: string;
  /** stored encrypted, never shown again */
  readonly secret?: boolean;
  readonly placeholder?: string;
  readonly optional?: boolean;
  /** not secret, but stored with the keys: asked again when the keys are replaced */
  readonly withKeys?: boolean;
  /** a choice instead of free text; the first option is the default */
  readonly options?: readonly { readonly value: string; readonly label: string }[];
  /** said under the field */
  readonly hint?: string;
}

interface ChannelForm {
  readonly name: string;
  readonly short: string;
  readonly account: Field;
  readonly fields: readonly Field[];
  readonly help: string;
  /** what the seller has to know before connecting */
  readonly notes?: readonly string[];
  /** form values → the API's { externalAccountId, config, secrets } */
  build(values: Record<string, string>): { externalAccountId: string; config: Record<string, unknown>; secrets: Record<string, string> };
}

const CHANNELS: Record<IntegratedChannel, ChannelForm> = {
  trendyol: {
    name: 'Trendyol',
    short: 'TY',
    account: { key: 'sellerId', label: 'Satıcı ID', placeholder: '734521' },
    fields: [
      { key: 'apiKey', label: 'API Key', secret: true },
      { key: 'apiSecret', label: 'API Secret', secret: true },
      {
        key: 'environment',
        label: 'Ortam',
        options: [
          { value: 'production', label: 'Canlı mağaza' },
          { value: 'stage', label: 'Test ortamı (stage; sunucu IP adresi Trendyol’a bildirilmeli)' },
        ],
      },
    ],
    help: 'Trendyol Satıcı Paneli → Hesap Bilgileri → Entegrasyon Bilgileri',
    notes: [
      'Anahtarları Trendyol’da “Pronto Depo” entegratörü için oluşturun; roller: Sipariş, Ürün, Teslimat ve Fatura Entegrasyonu.',
      'Kargo etiketi Trendyol’dan gelir (ortak barkod, “Trendyol öder” gönderilerde Trendyol Express ve Aras Kargo).',
      'Stok gönderimini, depodaki açılış stoğu girildikten sonra açın.',
    ],
    build: (v) => ({
      externalAccountId: v.sellerId!.trim(),
      config: { environment: v.environment || 'production' },
      secrets: { apiKey: v.apiKey!, apiSecret: v.apiSecret! },
    }),
  },
  hepsiburada: {
    name: 'Hepsiburada',
    short: 'HB',
    account: { key: 'merchantId', label: 'Merchant ID', placeholder: '4c1a8e2f-…' },
    fields: [
      // no user name to type: Hepsiburada takes the Merchant ID as the user and refuses any other name
      { key: 'secretKey', label: 'API şifresi (servis anahtarı)', secret: true },
      {
        key: 'userAgent',
        label: 'Entegratör adı (User-Agent)',
        withKeys: true,
        placeholder: 'Hepsiburada’nın e-postada verdiği entegratör adı',
        hint: 'Hepsiburada’nın yazdığı gibi, harfi harfine (büyük-küçük harf ve boşluklar dahil); farklı yazılırsa Hepsiburada bağlantıyı reddeder.',
      },
      {
        key: 'environment',
        label: 'Ortam',
        options: [
          { value: 'production', label: 'Canlı mağaza' },
          { value: 'test', label: 'Test ortamı (SIT)' },
        ],
      },
    ],
    help: 'Satıcı Paneli → Yardım Merkezi → Talepler → “API Entegrasyon – API Entegratör Yetkilendirme İşlemleri” talebiyle önce test (SIT), testler geçince canlı bilgileri verilir',
    notes: [
      'Hepsiburada’da “Otomatik paketleme” kapalı olmalı (Hesabım → Ayarlarım → Diğer ayarlar): paket, desi ve koli sayısıyla depoda oluşur. Açıksa Hepsiburada’nın oluşturduğu paket kullanılır.',
      'Siparişler Hepsiburada’nın anlaşmalı kargosuyla gider. Ortak barkod (Hepsiburada’nın verdiği kargo etiketi) HepsiJet ve Aras Kargo için çalışır; mağazanızda “kargo barkod” özelliği açık olmalı. Ortak barkod vermeyen firmalarda (Yurtiçi vb.) depo, paketin Hepsiburada barkoduyla kendi etiketini basar.',
      'Hepsiburada günde ürün sayınızın 10 katı kadar stok güncellemesine izin verir; sistem yalnız değişenleri gönderir.',
    ],
    build: (v) => ({
      externalAccountId: v.merchantId!.trim(),
      config: { environment: v.environment || 'production' },
      secrets: {
        username: v.merchantId!.trim(),
        password: v.secretKey!,
        userAgent: v.userAgent!.trim(),
      },
    }),
  },
  amazon: {
    name: 'Amazon SP-API',
    short: 'AZ',
    account: { key: 'sellerId', label: 'Selling Partner ID', placeholder: 'A2EUQ1WTGCTBG2' },
    fields: [
      { key: 'lwaClientId', label: 'LWA Client ID', secret: true },
      { key: 'lwaClientSecret', label: 'LWA Client Secret', secret: true },
      { key: 'refreshToken', label: 'Refresh token', secret: true },
      { key: 'marketplaceIds', label: 'Marketplace ID', placeholder: 'A33AVAJ2PDY3EV (Türkiye)' },
    ],
    help: 'Seller Central → Apps and Services → Develop Apps (SP-API uygulamanızın yetkilendirmesi)',
    build: (v) => ({
      externalAccountId: v.sellerId!.trim(),
      config: { region: 'eu', marketplaceIds: (v.marketplaceIds || 'A33AVAJ2PDY3EV').split(/[\s,]+/).filter(Boolean) },
      secrets: { lwaClientId: v.lwaClientId!, lwaClientSecret: v.lwaClientSecret!, refreshToken: v.refreshToken! },
    }),
  },
  shopify: {
    name: 'Shopify',
    short: 'SH',
    account: { key: 'shop', label: 'Mağaza alan adı', placeholder: 'magaza-adi.myshopify.com' },
    fields: [
      { key: 'accessToken', label: 'Admin API access token', secret: true },
      { key: 'webhookSecret', label: 'Webhook imza anahtarı', secret: true, optional: true },
    ],
    help: 'Shopify Admin → Settings → Apps and sales channels → Develop apps',
    build: (v) => ({
      externalAccountId: v.shop!.trim().toLowerCase(),
      config: { apiVersion: '2026-07', locationId: null },
      secrets: { accessToken: v.accessToken!, ...(v.webhookSecret ? { webhookSecret: v.webhookSecret } : {}) },
    }),
  },
  n11: {
    name: 'n11',
    short: 'n11',
    account: { key: 'store', label: 'Mağaza adı', placeholder: 'n11’deki mağaza adınız' },
    fields: [
      { key: 'appKey', label: 'API Anahtarı (App Key)', secret: true },
      { key: 'appSecret', label: 'API Şifresi (App Secret)', secret: true },
    ],
    help: 'n11 Mağaza Paneli (so.n11.com) → Hesabım → API Hesapları → Yeni Hesap Oluştur',
    notes: [
      'API hesabı, mağazanın evrakları (vergi levhası, KEP) onaylandıktan sonra açılabilir.',
      'Kargo n11’in anlaşmalı firmasıyla gider: n11’in verdiği kampanya kodu depo etiketine basılır. Teslimat şablonunda (Hesabım → Teslimat Bilgileri) gönderici ve iade adresi depo olmalı.',
      'Yeni siparişler depo toplamaya başlarken n11’de kendiliğinden onaylanır. Onaylanmamış bir siparişi reddetmek gerekirse n11 panelinden “Reddet” deyin; API bunu yapamaz.',
      'Fatura n11’e bağlantı olarak gider: e-Arşiv bağlantısı https ile başlamalı ve pdf, png ya da jpeg dosyasına gitmeli.',
      'Stok, ürünün n11’deki satıcı stok koduyla gönderilir; fiyatlarınıza dokunulmaz. “Konuma Özel Teslimat” siparişleri depodan gönderilemez.',
    ],
    build: (v) => ({ externalAccountId: v.store!.trim(), config: {}, secrets: { appKey: v.appKey!, appSecret: v.appSecret! } }),
  },
  ciceksepeti: {
    name: 'Çiçeksepeti',
    short: 'ÇS',
    account: { key: 'sellerId', label: 'Satıcı ID', placeholder: '150000123456' },
    fields: [
      { key: 'apiKey', label: 'API Key (x-api-key)', secret: true },
      {
        key: 'environment',
        label: 'Ortam',
        options: [
          { value: 'production', label: 'Canlı mağaza' },
          { value: 'test', label: 'Test ortamı (sandbox; ayrı test anahtarı gerekir)' },
        ],
      },
    ],
    help: 'Çiçeksepeti Satıcı Paneli → Hesap Yönetimi → Entegrasyon Bilgilerim (anahtar yoksa Destek → “API Entegrasyon Süreçleri” talebi)',
    notes: [
      'Entegrasyon Bilgilerim’deki “Entegratör Adı” alanına ProntoDepo yazın.',
      'Kargo modeliniz “Çiçeksepeti Kargo Entegrasyonu” ise kargo kodu Çiçeksepeti’nden gelir ve depo etiketine basılır; “Kendi Kargo Entegrasyonu” ise depo sizin kargo anlaşmanızla gönderir.',
      'Kargo firması yalnız sipariş “Yeni” iken değiştirilebilir. Tedarik edilemeyen bir siparişi Çiçeksepeti panelinden iptal edin; depo iptali kendisi görür.',
      'Çok ürünlü siparişlerde her alt sipariş ayrı koli ve ayrı kargo koduyla gider.',
      'Stok, Tedarikçi Varyant Kodu (stok kodu) ile gönderilir; fiyatlarınıza dokunulmaz, Çiçeksepeti en geç 4 saatte işler.',
    ],
    build: (v) => ({ externalAccountId: v.sellerId!.trim(), config: { environment: v.environment || 'production' }, secrets: { apiKey: v.apiKey! } }),
  },
  pttavm: {
    name: 'PttAVM',
    short: 'PTT',
    account: { key: 'sellerId', label: 'Satıcı ID', placeholder: '063167' },
    fields: [
      { key: 'apiKey', label: 'API Key', secret: true },
      { key: 'accessToken', label: 'Entegratör token’ı', secret: true },
      { key: 'warehouseId', label: 'PttAVM depo ID', optional: true, withKeys: true, placeholder: 'Boş bırakılırsa adında “Pronto” geçen depo' },
    ],
    help: 'PttAVM Satıcı Paneli (satici.pttavm.com) → Hesap Yönetimi → Entegrasyon Bilgileri',
    notes: [
      'Aynı ekranda “Pazaryeri Entegratörü” kartından entegratörü seçip token üretin; depo firması listede yoksa “Self Entegratör” kartını kullanın.',
      'Depo adresimizi PttAVM’de mağazanızın deposu olarak ekleyin; birden fazla deponuz varsa adında “Pronto” geçsin. PTT Kargo barkodunu depo oluşturur ve PTT’nin etiketini basar: depoya gelen siparişler için panelden barkod oluşturmayın.',
      'Ürünlerinizde “Kargo tedarikçiden” seçili olmasın. Tedarik edilemeyen bir siparişi PttAVM panelinden siz iptal edin.',
      'Stok, ürünün barkoduyla gönderilir (en fazla 9999 adet); fiyatlarınıza dokunulmaz.',
    ],
    build: (v) => ({
      externalAccountId: v.sellerId!.trim(),
      config: v.warehouseId?.trim() ? { warehouseId: Number(v.warehouseId.trim()) } : {},
      secrets: { apiKey: v.apiKey!, accessToken: v.accessToken! },
    }),
  },
  pazarama: {
    name: 'Pazarama',
    short: 'PZ',
    account: { key: 'sellerId', label: 'Satıcı ID', placeholder: 'bdd4d613-…' },
    fields: [
      { key: 'apiKey', label: 'API Key', secret: true },
      { key: 'apiSecret', label: 'API Secret', secret: true },
    ],
    help: 'Pazarama İş Ortağım (isortagim.pazarama.com) → Hesabım → Hesap Bilgileri → Entegrasyon Bilgileri',
    notes: [
      'API bilgileri yalnız bir kez gösterilir. Yeni anahtar üretmek eskisini geçersiz kılar: başka bir entegratör kullanıyorsanız ona da yenisini verin.',
      'API Secret en fazla 365 gün geçerlidir; süresi dolunca yenisini buraya girin.',
      'Pazarama anlaşmalı kargoda gönderi kodu (PZ…) Pazarama’dan gelir ve depo etiketine basılır. Pazarama’daki varsayılan gönderici adresiniz depo olmalı.',
      'Depoya gelen siparişleri panelden “Hazırlanıyor”a almayın; alıcının iptal taleplerini panelden onaylayın ya da reddedin (karar verilene kadar depo etiket basmaz).',
      'Stok, ürünün Pazarama’daki koduyla (barkod) gönderilir; fiyatlarınıza dokunulmaz.',
    ],
    build: (v) => ({ externalAccountId: v.sellerId!.trim(), config: {}, secrets: { apiKey: v.apiKey!, apiSecret: v.apiSecret! } }),
  },
  idefix: {
    name: 'İdefix',
    short: 'İD',
    account: { key: 'vendorId', label: 'Satıcı ID (Vendor ID)', placeholder: '12345' },
    fields: [
      { key: 'apiKey', label: 'API Key', secret: true },
      { key: 'apiSecret', label: 'API Secret Key', secret: true },
      {
        key: 'environment',
        label: 'Ortam',
        options: [
          { value: 'production', label: 'Canlı mağaza' },
          { value: 'test', label: 'Test ortamı (stage; sunucu IP adresi İdefix’e bildirilmeli)' },
        ],
      },
    ],
    help: 'İdefix Satıcı Paneli → Hesap Bilgilerim → Entegrasyon Bilgileri → Yeni API Oluştur (anahtarlar e-postanıza gelir)',
    notes: [
      'Kargo, İdefix’teki kargo profilinize göre gider: “Platform Öder” profillerde İdefix’in anlaşmalı firması (kodu İdefix verir, depo etiketine basılır), “Satıcı Öder” profillerde sizin kargo anlaşmanız.',
      'Stok, ürünün barkoduyla gönderilir; yalnız onaylı ürünlere stok gönderilebilir. İdefix stoğu fiyatla birlikte istediği için güncel fiyatınız aynen geri gönderilir.',
    ],
    build: (v) => ({ externalAccountId: v.vendorId!.trim(), config: { environment: v.environment || 'production' }, secrets: { apiKey: v.apiKey!, apiSecret: v.apiSecret! } }),
  },
  koctas: {
    name: 'Koçtaş',
    short: 'KÇ',
    account: { key: 'shopId', label: 'Mağaza ID', placeholder: '2000' },
    fields: [{ key: 'apiKey', label: 'API Anahtarı', secret: true }],
    help: 'Koçtaş satıcı paneli (koctas.mirakl.net) → Profilim → API Anahtarı; Mağaza ID: Ayarlar → Mağaza → Kimlik',
    notes: [
      'Mağazanız başka bir entegratöre bağlıysa yeni anahtar üretmeyin: Mirakl’da yeni anahtar eskisini geçersiz kılabilir.',
      'Siparişler kabul edilip ödemesi alındıktan sonra (Gönderilecek) depoya gelir: siparişleri panelden kabul edin ya da Koçtaş’tan otomatik kabulü açmasını isteyin.',
      'Koçtaş kargo etiketi vermez: depo varsayılan olarak sizin kargo anlaşmanızla gönderir ve takip numarasını Koçtaş’a bildirir.',
      'Fatura bağlantı olarak gönderilemez: e-Arşiv faturanızı panelden siparişe yükleyin. Stok, teklifin stok koduyla (offer SKU) gönderilir; fiyatlarınıza dokunulmaz.',
    ],
    build: (v) => ({ externalAccountId: v.shopId!.trim(), config: {}, secrets: { apiKey: v.apiKey! } }),
  },
  teknosa: {
    name: 'Teknosa',
    short: 'TK',
    account: { key: 'shopId', label: 'Mağaza ID', placeholder: '2000' },
    fields: [{ key: 'apiKey', label: 'API Anahtarı', secret: true }],
    help: 'Teknosa satıcı paneli (teknosatr-prod.mirakl.net) → Kullanıcı Profili → Profil → API anahtarı; Mağaza ID: Ayarlar → Mağaza → Kimlik',
    notes: [
      'Yeni anahtar üretmek eskisini geçersiz kılar: başka bir entegratör kullanıyorsanız onun bağlantısı kopar.',
      'Siparişler kabul edilip ödemesi alındıktan sonra (Gönderilecek) depoya gelir: siparişleri panelden kabul edin ya da Teknosa’dan otomatik kabulü açmasını isteyin.',
      'Teknosa kargo etiketi vermez: depo varsayılan olarak sizin kargo anlaşmanızla gönderir ve takip numarasını Teknosa’ya bildirir.',
      'Fatura bağlantı olarak gönderilemez: e-Arşiv faturanızı panelden siparişe yükleyin. Stok, teklifin stok koduyla (offer SKU) gönderilir; fiyatlarınıza dokunulmaz.',
    ],
    build: (v) => ({ externalAccountId: v.shopId!.trim(), config: {}, secrets: { apiKey: v.apiKey! } }),
  },
};

const STATUS: Record<CredentialView['status'], { label: string; tone: string }> = {
  active: { label: 'Aktif', tone: 'ok' },
  auth_failed: { label: 'Yetki hatası', tone: 'bad' },
  disabled: { label: 'Devre dışı', tone: '' },
};

/** Pazaryeri entegrasyon kasası: the seller's API keys, encrypted; connection test and status. */
export function Marketplaces({ onChange }: { onChange: () => void }) {
  const { me } = useSession();
  const toast = useToast();
  const list = useLoad(() => api<CredentialView[]>('GET', '/api/integrations/credentials'), []);
  const [adding, setAdding] = useState<IntegratedChannel | null>(null);
  const [rotating, setRotating] = useState<CredentialView | null>(null);
  const [removing, setRemoving] = useState<CredentialView | null>(null);
  const [testing, setTesting] = useState<string | null>(null);
  const [stockOf, setStockOf] = useState<CredentialView | null>(null);
  const [intakeOf, setIntakeOf] = useState<CredentialView | null>(null);
  const [openOf, setOpenOf] = useState<CredentialView | null>(null);
  const [demoHint, setDemoHint] = useState(false);
  const owner = me.role === 'owner';
  const canTest = me.role === 'owner' || me.role === 'manager';

  // the local demo talks to sandbox marketplaces: say which keys it accepts
  useEffect(() => {
    api('GET', '/api/demo')
      .then(() => setDemoHint(true))
      .catch(() => setDemoHint(false));
  }, []);

  const test = async (c: CredentialView) => {
    setTesting(c.id);
    try {
      const result = await api<{ ok: boolean; message: string | null }>('POST', `/api/integrations/credentials/${c.id}/verify`);
      toast(result.ok ? `${c.label}: bağlantı başarılı` : `${c.label}: ${result.message ?? 'bağlantı reddedildi'}`, result.ok ? 'ok' : 'bad');
    } catch (e) {
      toast(errorMessage(e), 'bad');
    } finally {
      setTesting(null);
      void list.reload();
      onChange();
    }
  };

  const toggle = async (c: CredentialView) => {
    try {
      await api('PATCH', `/api/integrations/credentials/${c.id}`, { status: c.status === 'disabled' ? 'active' : 'disabled' });
      void list.reload();
      onChange();
    } catch (e) {
      toast(errorMessage(e), 'bad');
    }
  };

  const idle = (list.data ?? []).filter((c) => c.status === 'active' && !c.ordersFrom);

  return (
    <>
      {idle.length > 0 && (
        <div className="banner info">
          <Icon name="alert" />
          <div>
            <b>{idle.map((c) => c.label).join(', ')}</b>: siparişler henüz depoya gelmiyor. Ürünleri eşleştirip stoğunuzu depoya
            getirdikten sonra “Sipariş alımını başlat”a basın; o zamana kadar siparişlerinizi kendiniz gönderirsiniz.
          </div>
        </div>
      )}
      <section className="card">
        <div className="card-h">
          <Icon name="plug" />
          <h2>Hesap bağla</h2>
          <span className="sub">Siparişleriniz bu hesaplardan otomatik çekilir, kargo ve fatura bilgisi geri bildirilir</span>
        </div>
        <div className="card-b stack">
          <div className="row">
            {(Object.keys(CHANNELS) as IntegratedChannel[]).map((channel) => (
              <button key={channel} className="btn" disabled={!owner} onClick={() => setAdding(channel)}>
                <span className={`channel ${channel}`} style={{ width: 26, height: 26, fontSize: 10 }}>
                  {CHANNELS[channel].short}
                </span>
                {CHANNELS[channel].name}
              </button>
            ))}
          </div>
          <div className="secret-note">
            <Icon name="check" size={16} /> Anahtarlar AES-256 ile şifrelenerek saklanır; kaydedildikten sonra panelde ve depoda kimse göremez.
          </div>
          {!owner && <div className="banner info">Hesap ekleme ve anahtar değiştirme yalnızca hesap sahibine açıktır.</div>}
          {demoHint && (
            <div className="banner warn">Demo ortamı gerçek pazaryerine bağlanmaz: “demo” ile başlayan anahtarlar geçerli sayılır, diğerleri yetki hatası verir.</div>
          )}
        </div>
      </section>

      <section className="card">
        <div className="card-h">
          <h2>Bağlı hesaplar</h2>
          <span className="sub">{list.data?.length ?? 0} hesap</span>
        </div>
        {list.error && <ErrorBanner message={list.error} onRetry={() => void list.reload()} />}
        {!list.data ? (
          <Spinner />
        ) : list.data.length === 0 ? (
          <Empty title="Henüz bağlı hesap yok">Yukarıdan bir pazaryeri seçerek API anahtarlarınızı girin.</Empty>
        ) : (
          <div className="card-b stack">
            {list.data.map((c) => (
              <div key={c.id} className="row" style={{ alignItems: 'flex-start', borderBottom: '1px solid var(--line)', paddingBottom: 12 }}>
                <span className={`channel ${c.channel}`}>{CHANNELS[c.channel].short}</span>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div className="row" style={{ gap: 8 }}>
                    <b>{c.label}</b>
                    <span className={`chip ${STATUS[c.status].tone}`}>{STATUS[c.status].label}</span>
                  </div>
                  <div className="muted" style={{ fontSize: 13 }}>
                    {CHANNELS[c.channel].name} · {CHANNELS[c.channel].account.label}: <span className="mono">{c.externalAccountId}</span>
                  </div>
                  <div className="muted" style={{ fontSize: 13 }}>
                    {c.lastVerifiedAt ? `Son başarılı test: ${fmt.dateTime(c.lastVerifiedAt)}` : 'Henüz başarılı test yok'}
                  </div>
                  <div className="muted" style={{ fontSize: 13 }}>
                    Siparişler:{' '}
                    {c.ordersFrom ? (
                      <>
                        depoya geliyor · başlangıç {fmt.dateTime(c.ordersFrom)}
                        {c.ordersSyncedUntil && ` · son kontrol ${fmt.dateTime(c.ordersSyncedUntil)}`}
                      </>
                    ) : (
                      <span className="chip warn">alım başlamadı</span>
                    )}
                  </div>
                  <div className="muted" style={{ fontSize: 13 }}>
                    {c.config.environment === 'stage' || c.config.environment === 'test' ? <span className="chip warn">Test ortamı</span> : null}{' '}
                    Stok gönderimi: {c.stockSyncEnabled ? `açık${c.stockSyncedAt ? ` · son ${fmt.dateTime(c.stockSyncedAt)}` : ''}` : 'kapalı'}
                    {c.stockSyncEnabled && c.stockSyncError && <span className="chip bad"> son gönderim başarısız</span>}
                  </div>
                  {c.status === 'auth_failed' && c.lastError && (
                    <div className="banner bad" style={{ marginTop: 8, padding: '8px 12px', fontWeight: 500 }}>
                      {c.lastError}
                    </div>
                  )}
                </div>
                <div className="row">
                  <button className={`btn sm${c.ordersFrom ? '' : ' primary'}`} disabled={!canTest || c.status !== 'active'} onClick={() => setIntakeOf(c)}>
                    <Icon name="list" size={16} /> {c.ordersFrom ? 'Sipariş alımı' : 'Sipariş alımını başlat'}
                  </button>
                  <button className="btn sm" disabled={c.status !== 'active'} onClick={() => setOpenOf(c)} title="Pazaryerinde açık olup depoya alınmamış siparişler">
                    <Icon name="inbox" size={16} /> Depoya alınmamış siparişler
                  </button>
                  <button className="btn sm" onClick={() => setStockOf(c)}>
                    <Icon name="stock" size={16} /> Stok gönderimi
                  </button>
                  <button className="btn sm" disabled={!canTest || testing === c.id} onClick={() => void test(c)}>
                    {testing === c.id ? 'Test ediliyor…' : 'Bağlantıyı test et'}
                  </button>
                  <button className="btn sm" disabled={!owner} onClick={() => setRotating(c)}>
                    Anahtarları güncelle
                  </button>
                  <button className="btn sm" disabled={!owner} onClick={() => void toggle(c)}>
                    {c.status === 'disabled' ? 'Etkinleştir' : 'Devre dışı bırak'}
                  </button>
                  <button className="icon-btn" disabled={!owner} onClick={() => setRemoving(c)} aria-label="Hesabı kaldır">
                    <Icon name="trash" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <ApiKeys />

      {adding && (
        <CredentialForm
          channel={adding}
          onClose={() => setAdding(null)}
          onSaved={(saved) => {
            setAdding(null);
            void list.reload().then(() => test(saved));
          }}
        />
      )}
      {rotating && (
        <CredentialForm
          channel={rotating.channel}
          existing={rotating}
          onClose={() => setRotating(null)}
          onSaved={(saved) => {
            setRotating(null);
            void list.reload().then(() => test(saved));
          }}
        />
      )}
      {intakeOf && (
        <OrderIntakeDialog
          credential={intakeOf}
          onClose={() => setIntakeOf(null)}
          onChange={() => {
            setIntakeOf(null);
            void list.reload();
            onChange();
          }}
        />
      )}
      {openOf && <OpenOrdersDialog credential={openOf} canTake={canTest} onClose={() => setOpenOf(null)} onTaken={onChange} />}
      {stockOf && (
        <StockSyncDialog
          credential={stockOf}
          onClose={() => setStockOf(null)}
          onChange={() => {
            void list.reload();
            onChange();
          }}
        />
      )}
      {removing && (
        <Modal
          title={`${removing.label} kaldırılsın mı?`}
          onClose={() => setRemoving(null)}
          footer={
            <>
              <button className="btn" onClick={() => setRemoving(null)}>
                Vazgeç
              </button>
              <button
                className="btn danger"
                onClick={async () => {
                  try {
                    await api('DELETE', `/api/integrations/credentials/${removing.id}`);
                    toast('Hesap kaldırıldı', 'ok');
                  } catch (e) {
                    toast(errorMessage(e), 'bad');
                  }
                  setRemoving(null);
                  void list.reload();
                  onChange();
                }}
              >
                Hesabı kaldır
              </button>
            </>
          }
        >
          <p>Bu hesaptan sipariş çekilmez; şifreli anahtarlar silinir. Daha önce aktarılan siparişler kalır.</p>
        </Modal>
      )}
    </>
  );
}

const STATE_WORDS: Readonly<Record<string, string>> = { Created: 'Yeni', Picking: 'İşleme alındı', Invoiced: 'Faturalandı' };

/**
 * Orders still open on the marketplace that the warehouse does not have (placed before intake, or
 * already started on by the seller): read fresh from the marketplace; one can be taken in.
 */
function OpenOrdersDialog(props: { credential: CredentialView; canTake: boolean; onClose: () => void; onTaken: () => void }) {
  const toast = useToast();
  const c = props.credential;
  const list = useLoad(() => api<OpenChannelOrder[]>('GET', `/api/integrations/credentials/${c.id}/open-orders`), [c.id]);
  const [taken, setTaken] = useState<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);
  const take = async (o: OpenChannelOrder) => {
    setBusy(o.orderNumber);
    try {
      await api('POST', `/api/integrations/credentials/${c.id}/open-orders/take`, { externalOrderId: o.externalOrderId, externalPackageId: o.externalPackageId });
      setTaken(new Set([...taken, `${o.externalOrderId}|${o.externalPackageId ?? ''}`]));
      toast(`${o.orderNumber} depoya alındı; Siparişler ekranında görünür`, 'ok');
      props.onTaken();
    } catch (e) {
      toast(errorMessage(e), 'bad');
    } finally {
      setBusy(null);
    }
  };
  const shown = (list.data ?? []).filter((o) => !taken.has(`${o.externalOrderId}|${o.externalPackageId ?? ''}`));
  return (
    <Modal title={`${c.label} · depoya alınmamış siparişler`} onClose={props.onClose} wide>
      <div className="stack">
        <div className="banner info-soft" style={{ display: 'block' }}>
          Pazaryerinde hâlâ açık olup depoda olmayan siparişler (son 14 gün): sipariş alımı başlamadan verilenler ya da sizin hazırlamaya
          başladıklarınız. <b>Kendiniz göndermeyecekseniz</b> depoya alın; gönderdiyseniz ya da göndereceksiniz almayın, yoksa sipariş iki kez gider.
        </div>
        {list.error && <ErrorBanner message={list.error} onRetry={() => void list.reload()} />}
        {!list.data ? (
          <Spinner />
        ) : shown.length === 0 ? (
          <Empty title="Depoya alınmamış açık sipariş yok">Pazaryerindeki açık siparişlerin hepsi depoda.</Empty>
        ) : (
          <div className="table-wrap">
            <table className="t">
              <thead>
                <tr>
                  <th>Sipariş</th>
                  <th>Durum</th>
                  <th>Ürünler</th>
                  <th>Tarih</th>
                  {props.canTake && <th />}
                </tr>
              </thead>
              <tbody>
                {shown.map((o) => (
                  <tr key={`${o.externalOrderId}|${o.externalPackageId ?? ''}`}>
                    <td className="mono">{o.orderNumber}</td>
                    <td>
                      <span className={`chip ${o.state === 'awaiting_fulfillment' ? 'info' : 'warn'}`}>{STATE_WORDS[o.channelStatus] ?? o.channelStatus}</span>
                    </td>
                    <td style={{ maxWidth: 320 }}>
                      {o.lines.map((l) => `${l.quantity} × ${l.name}`).join(', ')}
                    </td>
                    <td className="nowrap">
                      {fmt.dateTime(o.orderedAt)}
                      {o.shipBy && <div className="muted" style={{ fontSize: 12 }}>son kargo {fmt.dateTime(o.shipBy)}</div>}
                    </td>
                    {props.canTake && (
                      <td className="num">
                        <button className="btn sm primary" disabled={busy !== null} onClick={() => void take(o)}>
                          {busy === o.orderNumber ? 'Alınıyor…' : 'Depoya al'}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}

/**
 * Starts the account's orders coming to the warehouse, once the seller's goods are here, or stops
 * them. Before that the seller ships its marketplace orders itself, from its own stock.
 */
function OrderIntakeDialog(props: { credential: CredentialView; onClose: () => void; onChange: () => void }) {
  const toast = useToast();
  const c = props.credential;
  const [waiting, setWaiting] = useState(true);
  const [busy, setBusy] = useState(false);
  const started = c.ordersFrom !== null;
  const save = async (enabled: boolean) => {
    setBusy(true);
    try {
      await api('PUT', `/api/integrations/credentials/${c.id}/orders`, { enabled, waiting });
      toast(enabled ? `${c.label}: siparişler depoya gelmeye başladı` : `${c.label}: yeni siparişler artık depoya gelmeyecek`, 'ok');
      props.onChange();
    } catch (e) {
      toast(errorMessage(e), 'bad');
      setBusy(false);
    }
  };
  const channel = CHANNELS[c.channel].name;
  return (
    <Modal
      title={`${c.label} · sipariş alımı`}
      onClose={props.onClose}
      footer={
        <>
          <button className="btn" onClick={props.onClose}>
            Vazgeç
          </button>
          {started ? (
            <button className="btn danger" disabled={busy} onClick={() => void save(false)}>
              Sipariş alımını durdur
            </button>
          ) : (
            <button className="btn primary" disabled={busy} onClick={() => void save(true)}>
              Sipariş alımını başlat
            </button>
          )}
        </>
      }
    >
      {started ? (
        <div className="stack">
          <p>
            Siparişler depoya geliyor. Başlangıç: {fmt.dateTime(c.ordersFrom!)}
            {c.ordersWaiting ? ', o an bekleyen siparişlerle birlikte' : ''}.
          </p>
          <p className="muted">
            Durdurursanız yeni siparişler depoya gelmez, onları kendiniz gönderirsiniz. Depodaki açık siparişler hazırlanmaya devam eder;
            iptaller yine işlenir.
          </p>
        </div>
      ) : (
        <div className="stack">
          <p>
            Bu andan sonra {channel} hesabınıza gelen siparişler depoda toplanıp paketlenir ve kargoya verilir.
          </p>
          <ul className="muted" style={{ margin: 0, paddingLeft: 18 }}>
            <li>Ürünleriniz eşleşmiş ve stoğunuz depoda olmalı; stoğu olmayan sipariş “Stok bekliyor”da kalır.</li>
            <li>Hazırlamaya başladığınız (işleme aldığınız, faturaladığınız) siparişleri kendiniz tamamlayın.</li>
            <li>Kendi gönderdiğiniz bir sipariş, pazaryeri onu kargoda gösterince depoda kendiliğinden iptal olur.</li>
          </ul>
          <label className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
            <input type="checkbox" checked={waiting} onChange={(e) => setWaiting(e.target.checked)} style={{ marginTop: 4 }} />
            <span style={{ flex: '1 1 0', minWidth: 0 }}>
              Henüz dokunmadığım siparişler de gelsin
              <span className="muted" style={{ display: 'block', fontSize: 13 }}>
                Pazaryerinde “Yeni” durumunda bekleyen son 3 günün siparişleri (işleme alınmamış, etiketi basılmamış)
              </span>
            </span>
          </label>
        </div>
      )}
    </Modal>
  );
}

function CredentialForm(props: { channel: IntegratedChannel; existing?: CredentialView; onClose: () => void; onSaved: (credential: CredentialView) => void }) {
  const form = CHANNELS[props.channel];
  const [label, setLabel] = useState(props.existing?.label ?? `${form.name} mağazam`);
  const [values, setValues] = useState<Record<string, string>>(props.existing ? { [form.account.key]: props.existing.externalAccountId } : {});
  const [problem, setProblem] = useState<{ message: string; details: string[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const fields = props.existing ? form.fields.filter((f) => f.secret || f.withKeys) : [form.account, ...form.fields];
  const missing = fields.some((f) => !f.optional && !f.options && !values[f.key]?.trim());

  const save = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const built = form.build({ [form.account.key]: props.existing?.externalAccountId ?? '', ...values });
      const saved = props.existing
        ? await api<CredentialView>('PATCH', `/api/integrations/credentials/${props.existing.id}`, { label, secrets: built.secrets })
        : await api<CredentialView>('POST', '/api/integrations/credentials', { channel: props.channel, label, ...built });
      props.onSaved(saved);
    } catch (e) {
      setProblem({ message: errorMessage(e), details: errorDetails(e) });
      setBusy(false);
    }
  };

  return (
    <Modal
      title={props.existing ? `${props.existing.label} · anahtarları güncelle` : `${form.name} hesabı bağla`}
      onClose={props.onClose}
      footer={
        <>
          <button className="btn" onClick={props.onClose}>
            Vazgeç
          </button>
          <button className="btn primary" disabled={busy || missing || !label.trim()} onClick={() => void save()}>
            Kaydet ve bağlantıyı test et
          </button>
        </>
      }
    >
      <form className="stack" autoComplete="off" onSubmit={(e) => e.preventDefault()}>
        {problem && (
          <div className="banner bad">
            <div>
              {problem.message}
              {problem.details.length > 0 && <div style={{ fontWeight: 500 }}>{problem.details.join(' · ')}</div>}
            </div>
          </div>
        )}
        <div className="field">
          <label htmlFor="cred-label">Hesap adı</label>
          <input id="cred-label" className="input" value={label} onChange={(e) => setLabel(e.target.value)} />
        </div>
        {fields.map((f) => (
          <div key={f.key} className="field">
            <label htmlFor={`cred-${f.key}`}>
              {f.label}
              {f.optional && <span className="muted"> (isteğe bağlı)</span>}
            </label>
            {f.options ? (
              <select id={`cred-${f.key}`} className="select" value={values[f.key] ?? f.options[0]!.value} onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}>
                {f.options.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                id={`cred-${f.key}`}
                className={`input${f.secret ? '' : ' mono'}`}
                type={f.secret ? 'password' : 'text'}
                autoComplete={f.secret ? 'new-password' : 'off'}
                spellCheck={false}
                placeholder={f.placeholder}
                value={values[f.key] ?? ''}
                onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
              />
            )}
            {f.hint && <span className="hint">{f.hint}</span>}
          </div>
        ))}
        <p className="muted" style={{ fontSize: 13 }}>
          Nereden bulunur: {form.help}
        </p>
        {form.notes && (
          <ul className="muted" style={{ fontSize: 13, margin: 0, paddingLeft: 18 }}>
            {form.notes.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        )}
      </form>
    </Modal>
  );
}
